/* eslint-disable max-len */
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Form, Row } from 'antd';
import { RuleObject } from 'antd/es/form';
import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputNumberComponent from 'components/atoms/InputNumberComponent';
import InputTextArea from 'components/atoms/InputTextArea';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import UploadComponent, { IFileList } from 'components/atoms/UploadComponent';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';
import SaveButtonComponent from 'components/molecules/SaveButtonComponent';

import { IIdentifikaceEmitenta } from 'services/akcionar/emise/identifikaceEmitenta';
import isinUpload, { IIsinUpload } from 'services/akcionar/emise/isinUpload';
import {
    ESendEmisniUdaje,
    IEmisniUdaje,
    IListIsin,
    IPostupnesplaceni,
    emisniUdaje,
    getIsinList
} from 'services/akcionar/emise/novyIsinSend';
import zadostZmenaApi from 'services/akcionar/emise/zmenaISIN/zadostZmena';
import { IcisMeny } from 'services/cisMeny';
import fileDownload from 'services/fileDownload';
import { prepareFileIsinApi } from 'services/prepareFile';

import { EEmitentZadaOpridISIN } from 'enums/isin/emitentZadaOpridISIN';
import { EEvidenceCp } from 'enums/isin/evidenceCp';
import { ECisalikvynos } from 'enums/isin/udaje/cisalikvynos';
import { EDruhAkcie } from 'enums/isin/udaje/druhAkcie';
import { EDruhCertifikatu } from 'enums/isin/udaje/druhCertifikatu';
import { EDruhCp } from 'enums/isin/udaje/druhCp';
import { EDruhDluhopisu } from 'enums/isin/udaje/druhDluhopisu';
import { EEmisniPodminky } from 'enums/isin/udaje/emisniPodminky';
import { EFrekvenceVyplaty } from 'enums/isin/udaje/frekvenceVyplaty';
import { ESplaceni } from 'enums/isin/udaje/splaceni';
import { EUrokovaSazba } from 'enums/isin/udaje/urokovaSazba';
import { globalInputRegExp } from 'enums/regExp';
import { ETrueFalse } from 'enums/trueFalse';

import evidenceCpSelect, { evidenceCpSelectEditIsin } from 'data/codebooks/emise/evidenceCpSelect';
import alikVynosSelect, { alikVynosSelectZero } from 'data/codebooks/emise/udaje/alikVynosSelect';
import certifikatAktivitaAltSelect from 'data/codebooks/emise/udaje/certifikatAktivitaAltSelect';
import certifikatAktivitaSelect from 'data/codebooks/emise/udaje/certifikatAktivitaSelect';
import dlouheKratkeSelect from 'data/codebooks/emise/udaje/dlouheKratkeSelect';
// eslint-disable-next-line object-curly-newline
import druhAkcieSelect from 'data/codebooks/emise/udaje/druhAkcieSelect';
import druhCertifikatuSelect from 'data/codebooks/emise/udaje/druhCertifikatuSelect';
import {
    druhCpSelect,
    druhCpSelectAlt,
    druhCpSelectDLT,
    druhCpSelectJiny
} from 'data/codebooks/emise/udaje/druhCpSelect';
import druhDluhopisCfiSelect from 'data/codebooks/emise/udaje/druhDluhopisCfiSelect';
import druhDluhopisuSelect, { druhDluhopisuSelectDLT } from 'data/codebooks/emise/udaje/druhDluhopisuSelect';
import druhInvesticieSelect from 'data/codebooks/emise/udaje/druhInvesticieSelect';
import druhUplatneniSelect from 'data/codebooks/emise/udaje/druhUplatneniSelect';
import druhVynosuSelect from 'data/codebooks/emise/udaje/druhVynosuSelect';
import {
    emisniPodminkySelectNeumoznuje,
    emisniPodminkySelectUmoznuje
} from 'data/codebooks/emise/udaje/emisniPodminkySelect';
import formaCpSelect, { formaCpSelectAkcie } from 'data/codebooks/emise/udaje/formaCpSelect';
import frekvenceVyplatySelect from 'data/codebooks/emise/udaje/frekvenceVyplatySelect';
import hlasovaciPravaSelect from 'data/codebooks/emise/udaje/hlasovaciPravaSelect';
import konecVyporadaniSelect from 'data/codebooks/emise/udaje/konecVyporadaniSelect';
import nastrojKolInvNastrojSelect from 'data/codebooks/emise/udaje/nastrojKolInvNastrojSelect';
import otevrenyUzavrenySelect from 'data/codebooks/emise/udaje/otevrenyUzavrenySelect';
import podkladAktivumSelect from 'data/codebooks/emise/udaje/podkladAktivumSelect';
import pouzitiZiskuSelect from 'data/codebooks/emise/udaje/pouzitiZiskuSelect';
import pravidlaOdkupuSelect from 'data/codebooks/emise/udaje/pravidlaOdkupuSelect';
import splaceniSelect from 'data/codebooks/emise/udaje/splaceniSelect';
import typCennehoPapiruSelect from 'data/codebooks/emise/udaje/typCennehoPapiruSelect';
import typCertifikatuSelect from 'data/codebooks/emise/udaje/typCertifikatuSelect';
import typWarrantuSelect from 'data/codebooks/emise/udaje/typWarrantuSelect';
import typZavislostiSelect from 'data/codebooks/emise/udaje/typZavislostiSelect';
import { urokovaSazbaDluhopis } from 'data/codebooks/emise/udaje/urokovaSazbaSelectAlt';
import vyplatySelect from 'data/codebooks/emise/udaje/vyplatySelect';
import warrNakupProdejSelect from 'data/codebooks/emise/udaje/warrNakupProdejSelect';
import warrantOpceSelect from 'data/codebooks/emise/udaje/warrantOpceSelect';
import zarucenieEmiseSelect, {
    zarucenieEmisePodrizenySelect,
    zarucenieEmiseSelectAlt
} from 'data/codebooks/emise/udaje/zarucenieEmiseSelect';
import zasadyVyplatySelect from 'data/codebooks/emise/udaje/zasadyVyplatySelect';
import trueFalseSelect from 'data/codebooks/trueFalseSelect';

import formatInputNumberThousand from 'utils/formatInputNumberThousand';
import getLanguageObject from 'utils/getLanguageObject';
import dveDesetinneMistaValidation from 'utils/validations/dveDesetinneMistaValidation';
import { validateICO } from 'utils/validations/icoValidation';
import isinValidation from 'utils/validations/isinValidation';
import { validateLEI } from 'utils/validations/leiValidation';
import numberValidation from 'utils/validations/numberValidation';
import { procentaValidation6 } from 'utils/validations/procentaValidation';

import { IRozcestnikInput } from './IdentifikaceEmitentaNovy';

interface IRozcestnik {
    cisMeny: IcisMeny[];
    nextStep: () => void;
    prevStep: () => void;
    druhCP?: EDruhCp;
    setDruhCP: React.Dispatch<React.SetStateAction<EDruhCp | undefined>>;
    setIdentifikacePutData: React.Dispatch<React.SetStateAction<IIdentifikaceEmitenta | undefined>>;
    identifikacePutData?: IIdentifikaceEmitenta;
    setUdajeEmise: React.Dispatch<React.SetStateAction<IEmisniUdaje | undefined>>;
    udajeEmise?: IEmisniUdaje;
    rozcestnikInput?: IRozcestnikInput;
    setRozcestnikInput: React.Dispatch<React.SetStateAction<IRozcestnikInput | undefined>>;
    zadostID?: number;
    prevStepRef: number;
    zmenaIsin?: boolean;
    displayOnly?: boolean;
    setModalMessage?: React.Dispatch<React.SetStateAction<string>>;
}

const UdajeEmise: React.FC<IRozcestnik> = ({
    cisMeny,
    nextStep,
    prevStep,
    setIdentifikacePutData,
    identifikacePutData,
    rozcestnikInput,
    setRozcestnikInput,
    zadostID,
    udajeEmise,
    druhCP,
    setDruhCP,
    setUdajeEmise,
    prevStepRef,
    zmenaIsin,
    displayOnly,
    setModalMessage
}) => {
    const [form] = Form.useForm();
    const { language, menuProfile } = useContext(Context);
    const [loading, setLoading] = useState<boolean>(false);
    const [uploadLoading, setUploadLoading] = useState<boolean>(false);
    const [editLoading, setEditLoading] = useState<boolean>(false);
    const setForceRerender = useState<boolean>(false)[1];
    const [fileList, setFileList] = useState<IFileList[] | undefined>([]);
    const [prilozeneSoubory, setPrilozeneSoubory] = useState<IIsinUpload[] | undefined>([]);
    const [isFirstStep, setIsFirstStep] = useState<boolean>(displayOnly ? false : prevStepRef !== 3);
    const [saving, setSaving] = useState<boolean>(false);
    const [saveModalMessage, setSaveModalMessage] = useState<string>('');
    const [podkladAktiva, setPodkladAktiva] = useState<string[]>(
        udajeEmise?.podkladaktiva?.length && typeof udajeEmise?.podkladaktiva === 'object'
            ? udajeEmise?.podkladaktiva?.map(e => e.isin)
            : ['']
    );
    const [postupnesplaceniData, setPostupnesplaceniData] = useState<IPostupnesplaceni[]>(
        udajeEmise?.postupnesplaceni || []
    );
    const [zvolenyIsin, setZvolenyIsin] = useState<IListIsin>();
    const [listISIN, setListISIN] = useState<IListIsin[]>([]);
    const [isRozpracovanaZmena, setIsRozpracovanaZmena] = useState<boolean>(false);

    const onCelkovaHodnotaEmiseChange = () => {
        // pouzite pri vypocte celkovej hodnoty akcie
        const hodnota = (
            (parseFloat(form.getFieldValue('nbti')) || 0) * (parseFloat(form.getFieldValue('mtno')) || 0)
        ).toFixed(2);
        form.setFieldsValue({
            ignore2: formatInputNumberThousand(hodnota)
        });
    };

    onCelkovaHodnotaEmiseChange(); // todo vymysliet lepsie (inak sa mi neda nastavit hodnotu pri nacitani rozpracovanej ziadosti)
    useEffect(() => {
        if (zadostID) {
            // nacita uploadnute suobry a data pri rozpracovanej ziadosti
            if (zmenaIsin) {
                setLoading(true);
                getIsinList()
                    .then(resp => {
                        setListISIN(resp.isininfos ? resp.isininfos : []);
                        zadostZmenaApi('GET', zadostID!, 'Zmena', 'Zmena')
                            .then(zadostData => {
                                const selectedIsin = resp.isininfos.filter(e => e.cval === zadostData.cval)[0];
                                setZvolenyIsin(selectedIsin);
                                setIsRozpracovanaZmena(true);
                                form.setFieldsValue({
                                    duvod: zadostData.duvod,
                                    isin: selectedIsin.cval
                                });
                            })
                            .finally(() => setLoading(false));
                    })
                    .catch(() => setLoading(false));
            }
            isinUpload('get', zadostID ? zadostID : 0).then(resp => {
                setPrilozeneSoubory(resp.soubor);
                setFileList(
                    resp.soubor &&
                        resp.soubor.map((e, i) => {
                            return {
                                uid: String(i),
                                oid: e.oid ? e.oid : 0,
                                name: e.orignazev ? e.orignazev : 'bez nazvu',
                                fileName: e.tempnazev ? e.tempnazev : 'a',
                                type: e.orignazev ? e.orignazev.split('.').slice(-1)[0] : '',
                                size: 0,
                                status: 'done'
                            };
                        })
                );
            });
            if (identifikacePutData?.druhcp && zadostID) {
                setLoading(true);
                emisniUdaje('get', ESendEmisniUdaje[identifikacePutData.druhcp], zadostID)
                    .then(data => {
                        setUdajeEmise(data);
                        const dataWithoutDatumSplatnosti = {
                            ...data
                        };
                        delete dataWithoutDatumSplatnosti.datumsplatnosti;
                        form.setFieldsValue({
                            ...dataWithoutDatumSplatnosti,
                            emisnipodminky: data?.emisnipodminky,
                            druhcertifikatu: data?.druhcertifikatu,
                            splaceni: data?.splaceni,
                            urokovasazba: data?.urokovasazba,
                            alikvynos: data?.alikvynos,
                            druhdluhopiscfi: data?.druhdluhopiscfi,
                            druhdluhopisu: [EDruhDluhopisu.SC, EDruhDluhopisu.SS].includes(
                                data?.druhdluhopisu as EDruhDluhopisu
                            )
                                ? EDruhDluhopisu.SD
                                : data?.druhdluhopisu,
                            druhwarrantu: data?.druhwarrantu,
                            oddelenikuponu: data?.oddelenikuponu,
                            ompr: typeof udajeEmise?.ompr === 'boolean' ? String(udajeEmise.ompr) : udajeEmise?.ompr,
                            pridelitnavse: data?.pridelitnavse ? String(data.pridelitnavse) : undefined,
                            podminkysdatumem:
                                typeof udajeEmise?.podminkysdatumem === 'boolean'
                                    ? String(data.podminkysdatumem)
                                    : druhCP === EDruhCp.DLUHOPIS
                                      ? udajeEmise?.perpetuitni === 'boolean'
                                          ? String(!udajeEmise?.perpetuitni)
                                          : 'true'
                                      : undefined
                        });
                        data?.podkladaktiva &&
                            setPodkladAktiva(data?.podkladaktiva.length ? data?.podkladaktiva?.map(e => e.isin) : ['']);
                        data?.postupnesplaceni && setPostupnesplaceniData(data?.postupnesplaceni);
                    })
                    .catch(() => {
                        /* identifikacePutData?.druhcp === EDruhCp.DLUHOPIS &&
                            form.setFieldsValue({
                                podminkysdatumem: 'true'
                            }); */
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        }
        form.setFieldsValue(
            (druhCP || identifikacePutData?.druhcp) === EDruhCp.PREDNOSTNIPRAVO ||
                (druhCP || identifikacePutData?.druhcp) === EDruhCp.PRAVOUPSANI ||
                (druhCP || identifikacePutData?.druhcp) === EDruhCp.ZATIMNILISTY ||
                (druhCP || identifikacePutData?.druhcp) === EDruhCp.OSTATNI
                ? {
                      druhcp: EDruhCp.JINYINVNASTROJ,
                      typcp: druhCP || identifikacePutData?.druhcp,
                      evidencecp: rozcestnikInput?.evidencecp || udajeEmise?.evidencecp
                  }
                : {
                      druhcp: druhCP || identifikacePutData?.druhcp,
                      evidencecp: rozcestnikInput?.evidencecp || udajeEmise?.evidencecp
                  }
        );
        if (displayOnly) {
            form.setFieldsValue({
                druhcertifikatu: udajeEmise?.druhcertifikatu,
                splaceni: udajeEmise?.splaceni,
                urokovasazba: udajeEmise?.urokovasazba,
                alikvynos: udajeEmise?.alikvynos,
                druhdluhopiscfi: udajeEmise?.druhdluhopiscfi,
                druhdluhopisu: [EDruhDluhopisu.SC, EDruhDluhopisu.SS].includes(
                    udajeEmise?.druhdluhopisu as EDruhDluhopisu
                )
                    ? EDruhDluhopisu.SD
                    : udajeEmise?.druhdluhopisu,
                druhwarrantu: udajeEmise?.druhwarrantu,
                oddelenikuponu:
                    typeof udajeEmise?.oddelenikuponu === 'boolean'
                        ? String(udajeEmise.oddelenikuponu)
                        : udajeEmise?.oddelenikuponu,
                ompr: typeof udajeEmise?.ompr === 'boolean' ? String(udajeEmise.ompr) : udajeEmise?.ompr,
                emisnipodminky: udajeEmise?.emisnipodminky,
                pridelitnavse: udajeEmise?.pridelitnavse ? String(udajeEmise.pridelitnavse) : undefined,
                podminkysdatumem:
                    typeof udajeEmise?.podminkysdatumem === 'boolean'
                        ? String(udajeEmise.podminkysdatumem)
                        : typeof udajeEmise?.perpetuitni === 'boolean'
                          ? udajeEmise?.perpetuitni
                              ? 'false'
                              : 'true'
                          : undefined
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinish = (value: any) => {
        if (isFirstStep && !saving) {
            if (zmenaIsin) {
                zadostZmenaApi('PUT', zadostID!, 'Zmena', 'Zmena', {
                    cval: zvolenyIsin?.cval
                }).then(() =>
                    emisniUdaje('get', ESendEmisniUdaje[zvolenyIsin?.druhcp!], zadostID!)
                        .then(data => {
                            const zvolenyIsinData = zvolenyIsin?.data && JSON.parse(zvolenyIsin?.data);
                            setUdajeEmise(data);
                            form.setFieldsValue({
                                druhdluhopisu: [EDruhDluhopisu.SC, EDruhDluhopisu.SS].includes(
                                    data?.druhdluhopisu as EDruhDluhopisu
                                )
                                    ? EDruhDluhopisu.SD
                                    : data?.druhdluhopisu,
                                druhcertifikatu: data?.druhcertifikatu,
                                splaceni: data?.splaceni,
                                ignore3:
                                    data?.podobacp || zvolenyIsinData.podobacp
                                        ? getLanguageObject(language)[
                                              `ISIN_ROZCESTNIK_PRIDELENI_ISIN_${(data?.podobacp || zvolenyIsinData.podobacp)!.toUpperCase()}`
                                          ]
                                        : '',
                                evidencecp: data.evidencecp || zvolenyIsinData.evidencecp
                            });
                            data?.podkladaktiva &&
                                setPodkladAktiva(
                                    data?.podkladaktiva.length ? data?.podkladaktiva?.map(e => e.isin) : ['']
                                );
                            setIdentifikacePutData(prevState => {
                                return {
                                    ...prevState,
                                    evidencecp: data.evidencecp || zvolenyIsinData.evidencecp,
                                    podobacp: data.podobacp || zvolenyIsinData.podobacp
                                };
                            });
                            setDruhCP(zvolenyIsin?.druhcp);
                            setIsRozpracovanaZmena(true);
                            setIsFirstStep(false);
                        })
                        .finally(() => {
                            setLoading(false);
                        })
                );
            } else {
                const cp: EDruhCp = value.typcp || value.druhcp;
                if (cp && identifikacePutData?.druhcp && cp !== identifikacePutData?.druhcp) {
                    setUdajeEmise(undefined);
                    form.resetFields();
                    setTimeout(
                        () =>
                            form.setFieldsValue(
                                cp === EDruhCp.PREDNOSTNIPRAVO ||
                                    cp === EDruhCp.PRAVOUPSANI ||
                                    cp === EDruhCp.ZATIMNILISTY ||
                                    cp === EDruhCp.OSTATNI
                                    ? {
                                          druhcp: EDruhCp.JINYINVNASTROJ,
                                          typcp: cp,
                                          evidencecp: rozcestnikInput?.evidencecp || udajeEmise?.evidencecp
                                      }
                                    : {
                                          druhcp: cp,
                                          evidencecp: rozcestnikInput?.evidencecp || udajeEmise?.evidencecp
                                      }
                            ),
                        0
                    );
                }
                setDruhCP(value.druhcp);
                setIdentifikacePutData(prevState => {
                    return {
                        ...prevState,
                        ...(prevState?.evidencecp && {
                            evidencecp: prevState?.evidencecp
                        }),
                        druhcp: cp
                    };
                });
                setIsFirstStep(false);
            }
        } else if (zmenaIsin && value.evidencecp === EEvidenceCp.CDCP && dayjs(menuProfile?.datplatamlz) < dayjs()) {
            setModalMessage?.('ZADOST_O_ISIN_AMLZ_CDCP');
        } else {
            setLoading(true);
            /* eslint-disable no-unused-vars */
            /* eslint-disable @typescript-eslint/no-unused-vars */
            const { druhcp, typcp, ignore2, ignore3, identifikaceosoby, duvod, ...dataToSend } = value;
            const podkladAktivaSend: string[] = [];
            const postupnesplaceniDataSend: IPostupnesplaceni[] = [];
            podkladAktiva?.map((_e, i) => {
                !!dataToSend[`ignoreISIN${i}`] && podkladAktivaSend.push(dataToSend[`ignoreISIN${i}`]);
                delete dataToSend[`ignoreISIN${i}`];
            });
            postupnesplaceniData?.map((_e, i) => {
                postupnesplaceniDataSend.push({
                    ...(dataToSend[`datumsplatnosti${i}`] && {
                        datumsplatnosti: dataToSend[`datumsplatnosti${i}`].format('YYYY-MM-DD')
                    }),
                    ...(dataToSend[`mtno${i}`] && {
                        mtno: dataToSend[`mtno${i}`]
                    }),
                    ...(dataToSend[`dnom${i}`] && {
                        dnom: dataToSend[`dnom${i}`]
                    })
                });
                delete dataToSend[`datumsplatnosti${i}`];
                delete dataToSend[`mtno${i}`];
                delete dataToSend[`dnom${i}`];
            });
            const data = {
                ...dataToSend,
                ...(dataToSend.datumsplatnosti && {
                    datumsplatnosti: dataToSend.datumsplatnosti.format('YYYY-MM-DD')
                }),
                ...(dataToSend.druhdluhopisu && {
                    druhdluhopisu:
                        dataToSend.evidencecp === EEvidenceCp.MF && dataToSend.druhdluhopisu === EDruhDluhopisu.SD
                            ? dataToSend.vymenitelny
                                ? EDruhDluhopisu.SC
                                : EDruhDluhopisu.SS
                            : dataToSend.druhdluhopisu
                }),
                ...((identifikacePutData?.podobacp || rozcestnikInput?.podobacp || dataToSend.podobacp) && {
                    podobacp: dataToSend.podobacp || identifikacePutData?.podobacp || rozcestnikInput?.podobacp
                }),
                ...(dataToSend.ompr && {
                    ompr: dataToSend.ompr === ETrueFalse.TRUE
                }),
                ...(dataToSend.podminkysdatumem && {
                    podminkysdatumem: dataToSend.podminkysdatumem === ETrueFalse.TRUE
                }),
                ...(dataToSend.oddelenikuponu && {
                    oddelenikuponu: dataToSend.oddelenikuponu === ETrueFalse.TRUE
                }),
                ...(dataToSend.pridelitnavse && {
                    pridelitnavse: dataToSend.pridelitnavse === ETrueFalse.TRUE
                }),
                ...(dataToSend.predcassplatnost && {
                    predcassplatnost: dataToSend.predcassplatnost === ETrueFalse.TRUE
                }),
                ...(((druhcp === EDruhCp.INVCERT && dataToSend.evidencecp === EEvidenceCp.CDCP) ||
                    druhCP === EDruhCp.WARRANT) &&
                    podkladAktiva.length &&
                    podkladAktivaSend.length && {
                        podkladaktiva: podkladAktivaSend.map(e => {
                            return {
                                isin: e
                            };
                        })
                    }),
                ...((dataToSend.emisnipodminky === EEmisniPodminky.UPSUMJ ||
                    dataToSend.emisnipodminky === EEmisniPodminky.UPEMPO ||
                    dataToSend.emisnipodminky === EEmisniPodminky.UPDMPO ||
                    dataToSend.emisnipodminky === EEmisniPodminky.UPEDPO) &&
                    druhcp === EDruhCp.DLUHOPIS &&
                    postupnesplaceniData.length && {
                        postupnesplaceni: postupnesplaceniDataSend
                    })
            };
            setUdajeEmise(data);
            (druhCP || druhcp || typcp) &&
                emisniUdaje(
                    'put',
                    ESendEmisniUdaje[((typcp || druhcp) as EDruhCp) || druhCP],
                    zadostID ? zadostID : 0,
                    data
                )
                    .then(() => {
                        duvod &&
                            zadostZmenaApi('PUT', zadostID!, 'Zmena', 'Zmena', {
                                cval: zvolenyIsin?.cval,
                                duvod: duvod
                            });
                        !saving && nextStep();
                        saving && setSaveModalMessage('ULOZENI_DAT_200');
                        setSaving(false);
                        setLoading(false);
                    })
                    .catch(() => {
                        saving && setSaveModalMessage('CHYBA_ULOZENI_DAT');
                        setLoading(false);
                        setSaving(false);
                    });
        }
    };

    useEffect(() => {
        saving && onFinish(form.getFieldsValue());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saving]);

    useEffect(() => {
        setTimeout(() => setForceRerender(pv => !pv), 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFirstStep]);

    useEffect(() => {
        setEditLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [udajeEmise]);

    const rerender = () => {
        setForceRerender(val => !val);
    };

    const uploadProps = {
        showDownloadIcon: true,
        onChange: (data: UploadChangeParam) => {
            if (data.file.status) setUploadLoading(true);
            if (data.fileList)
                setFileList(
                    data.fileList
                        .filter(f => f.status && f.status !== 'error')
                        .map(e => {
                            return {
                                // oid: resp.id!,
                                uid: e.uid!,
                                name: e.name.normalize()!,
                                fileName: e.fileName ? e.fileName : e.response ? e.response.tempnazev : ''!,
                                type: e.type!,
                                size: e.size!,
                                status: e.status ? e.status : 'error'
                            };
                        })
                );
            if (data.file.response && data.fileList) {
                return isinUpload('post', zadostID ? zadostID : 0, {
                    tempnazev: data.file.response.tempnazev,
                    orignazev: data.file.name
                })
                    .then(resp => {
                        setPrilozeneSoubory(
                            data.fileList.map(e => {
                                return {
                                    orignazev: e.name.normalize(),
                                    tempnazev: e.response ? e.response.tempnazev : e.fileName,
                                    uid: e.uid,
                                    oid: resp.id
                                };
                            })
                        );

                        form.validateFields(['identifikaceosoby']);
                    })
                    .finally(() => setUploadLoading(false));
            }
        },
        onRemove: (file: UploadFile) => {
            if (fileList?.length) {
                const oldState = [...fileList];
                const toDelete = oldState.filter(e => e.uid === file.uid);
                const newState = oldState.filter(e => e.uid !== file.uid);
                isinUpload('delete', zadostID ? zadostID : 0, undefined, toDelete[0].oid).then(() =>
                    setUploadLoading(false)
                );
                setFileList(newState);
                const newSoubory = prilozeneSoubory
                    ? [...prilozeneSoubory].filter(e => e.tempnazev !== file.fileName)
                    : [];
                setPrilozeneSoubory(newSoubory);
            }
        },
        onDownload: (file: UploadFile) =>
            prepareFileIsinApi(zadostID!, file.fileName ? file.fileName : '').then(resp => {
                fileDownload(resp.redirect, file.name ? file.name : 'file');
            }),
        fileList: fileList,
        buttonDisabled: fileList ? fileList.length > 4 : false,
        buttonLoading: uploadLoading
    };

    const renderPodkladAktiva = () =>
        (podkladAktiva || []).map((e, i) => (
            <InputComponent
                label="ISIN_PODKLADOVEHO_AKTIVA"
                name={`ignoreISIN${i}`}
                key={`ignoreISIN${i}`}
                maxLength={12}
                notRequired={true}
                rules={isinValidation()}
                capitalize={true}
                initialValue={e}
                labelExtra={
                    (i === podkladAktiva.length - 1 || i === 0) && !displayOnly ? (
                        <Button
                            id="c2PlusAltBtn"
                            htmlType="button"
                            type="primary"
                            style={{
                                display: 'inline-block',
                                marginLeft: 20,
                                width: 50
                            }}
                            disabled={false}
                            icon={i === 0 ? <PlusOutlined /> : <MinusOutlined />}
                            onClick={() => {
                                if (i === 0) setPodkladAktiva(pv => [...pv, '']);
                                else {
                                    form.setFieldsValue({
                                        [`ignoreISIN${i}`]: undefined
                                    });
                                    setPodkladAktiva(pv => pv.filter((_e, index) => i !== index));
                                }
                            }}
                        />
                    ) : null
                }
                disabled={displayOnly}
            />
        ));

    return (
        <LoadingWrapper loading={loading || editLoading}>
            {(druhCP || form.getFieldValue('druhcp')) && !displayOnly && (
                <SaveButtonComponent
                    onClick={() => {
                        setSaving(true);
                    }}
                    modalText={saveModalMessage}
                    onModalClose={() => setSaveModalMessage('')}
                />
            )}
            <FormWrapper outForm={form} onFinish={onFinish} preserve={true}>
                {!loading &&
                    !editLoading &&
                    (zmenaIsin && isFirstStep ? (
                        <>
                            <SelectComponent
                                name="isin"
                                labelParagraph={'ISIN'}
                                data={(listISIN || []).map(e => {
                                    return {
                                        labelcz: e.cval,
                                        value: e.cval
                                    };
                                })}
                                defaultValue={zvolenyIsin?.cval}
                                isFormItem={true}
                                disabled={(isRozpracovanaZmena && !!zvolenyIsin) || displayOnly}
                                onChange={val => {
                                    const data = listISIN.filter(e => e.cval === val)[0];
                                    setZvolenyIsin(data);
                                    setRozcestnikInput(pv => {
                                        return {
                                            emitentje: menuProfile?.typemitenta || pv?.emitentje,
                                            evidencecp: data.pcot as EEvidenceCp,
                                            podobacp: data.tval
                                        };
                                    });
                                }}
                            />
                        </>
                    ) : (
                        <>
                            <SelectComponent
                                name="druhcp"
                                labelParagraph="ISIN_EMISE_DRUH"
                                isFormItem={true}
                                disabled={(!isFirstStep && !!druhCP) || displayOnly}
                                defaultValue={druhCP || identifikacePutData?.druhcp}
                                data={
                                    (rozcestnikInput?.evidencecp === EEvidenceCp.CDCPDLT ||
                                        udajeEmise?.evidencecp === EEvidenceCp.CDCPDLT) &&
                                    !displayOnly
                                        ? druhCpSelectDLT
                                        : rozcestnikInput?.emitentje === 'FO' && !displayOnly
                                          ? druhCpSelectAlt
                                          : druhCpSelect
                                }
                                onChange={rerender}
                            />
                            {form.getFieldValue('druhcp') === EDruhCp.NASTROJKOLINV && (
                                <SelectComponent
                                    name="druhnastroje"
                                    labelParagraph="ISIN_EMISE_DRUH_NASTROJKOLINV_TYP"
                                    isFormItem={true}
                                    disabled={(!isFirstStep && !!druhCP) || displayOnly}
                                    defaultValue={
                                        druhCP === EDruhCp.NASTROJKOLINV ? udajeEmise?.druhnastroje : undefined
                                    }
                                    data={nastrojKolInvNastrojSelect}
                                    onChange={rerender}
                                />
                            )}
                            {form.getFieldValue('druhcp') === EDruhCp.JINYINVNASTROJ && (
                                <SelectComponent
                                    name="typcp"
                                    labelParagraph="ISIN_EMISE_DRUH_TYP"
                                    isFormItem={true}
                                    disabled={(!isFirstStep && !!druhCP) || displayOnly}
                                    defaultValue={
                                        druhCP === EDruhCp.JINYINVNASTROJ ? identifikacePutData?.druhcp : undefined
                                    }
                                    data={druhCpSelectJiny}
                                    onChange={rerender}
                                />
                            )}
                        </>
                    ))}

                {!isFirstStep &&
                    druhCP &&
                    (druhCP === EDruhCp.JINYINVNASTROJ ||
                        druhCP === EDruhCp.PREDNOSTNIPRAVO ||
                        druhCP === EDruhCp.PRAVOUPSANI ||
                        druhCP === EDruhCp.ZATIMNILISTY ||
                        druhCP === EDruhCp.OSTATNI) &&
                    form.getFieldValue('druhakcie') !== EDruhAkcie.JINA && (
                        <InputTextArea
                            rows={4}
                            name="jinepopis"
                            initialValue={udajeEmise?.jinepopis}
                            label="POPIS"
                            disabled={displayOnly}
                        />
                    )}
                {!isFirstStep &&
                    druhCP &&
                    druhCP !== EDruhCp.JINYINVNASTROJ &&
                    druhCP !== EDruhCp.PREDNOSTNIPRAVO &&
                    druhCP !== EDruhCp.PRAVOUPSANI &&
                    druhCP !== EDruhCp.ZATIMNILISTY &&
                    druhCP !== EDruhCp.OSTATNI && (
                        <>
                            {druhCP === EDruhCp.AKCIE && (displayOnly ? !!udajeEmise?.druhakcie : true) && (
                                <SelectComponent
                                    isFormItem={true}
                                    name="druhakcie"
                                    defaultValue={udajeEmise?.druhakcie}
                                    labelParagraph="ISIN_EMISE_AKCIE_DRUH"
                                    onChange={value => {
                                        rerender();
                                        value === EDruhAkcie.KUS || value === EDruhAkcie.PRIORKUS
                                            ? form.setFieldsValue({
                                                  mtno: 0,
                                                  ignore2: 0
                                              })
                                            : form.setFieldsValue({
                                                  mtno: undefined
                                              });
                                    }}
                                    data={druhAkcieSelect}
                                    disabled={displayOnly}
                                />
                            )}
                            {(form.getFieldValue('druhakcie') === EDruhAkcie.PRIOR ||
                                form.getFieldValue('druhakcie') === EDruhAkcie.PRIORKUS) && (
                                <SelectComponent
                                    isFormItem={true}
                                    name="pravidlaodkupu"
                                    defaultValue={udajeEmise?.pravidlaodkupu}
                                    labelParagraph="ISIN_EMISE_AKCIE_ODKUP"
                                    data={pravidlaOdkupuSelect}
                                    disabled={displayOnly}
                                />
                            )}
                            {(form.getFieldValue('druhakcie') === EDruhAkcie.PRIOR ||
                                form.getFieldValue('druhakcie') === EDruhAkcie.PRIORKUS) && (
                                <SelectComponent
                                    name="druhvynosu"
                                    defaultValue={udajeEmise?.druhvynosu}
                                    isFormItem={true}
                                    labelParagraph="ISIN_EMISE_AKCIE_VYNOS"
                                    data={druhVynosuSelect}
                                    disabled={displayOnly}
                                />
                            )}
                            {druhCP === EDruhCp.DLUHOPIS && (
                                <>
                                    <SelectComponent
                                        isFormItem={true}
                                        name="druhdluhopisu"
                                        defaultValue={udajeEmise?.druhdluhopisu}
                                        infoTooltipText={
                                            rozcestnikInput?.emitentje === 'FO' && !displayOnly
                                                ? 'ISIN_EMISE_DLUHOPIS_DRUH_HELP'
                                                : undefined
                                        }
                                        labelParagraph="ISIN_EMISE_DLUHOPIS_DRUH"
                                        data={
                                            form.getFieldValue('evidencecp') === EEvidenceCp.CDCPDLT
                                                ? druhDluhopisuSelectDLT
                                                : druhDluhopisuSelect
                                        }
                                        onChange={value => {
                                            form.setFieldsValue({
                                                druhdluhopiscfi:
                                                    value === EDruhDluhopisu.ZL
                                                        ? EDruhDluhopisu.HZL
                                                        : value === EDruhDluhopisu.KO
                                                          ? EDruhDluhopisu.KD
                                                          : form.getFieldValue('vymenitelny')
                                                            ? EDruhDluhopisu.VD
                                                            : udajeEmise?.druhdluhopiscfi
                                            });
                                            rerender();
                                        }}
                                        rules={[
                                            {
                                                required: true,
                                                message: <TranslateText>NEVALIDNI_POVINNA_POLOZKA</TranslateText>
                                            },
                                            () => ({
                                                validator() {
                                                    if (
                                                        form.getFieldValue('druhdluhopiscfi') === EDruhDluhopisu.HZL &&
                                                        form.getFieldValue('podminkysdatumem') === ETrueFalse.FALSE
                                                    )
                                                        return Promise.reject(
                                                            <TranslateText>
                                                                ISIN_EMISE_DLUHOPIS_TYP_NEVHODNE_ZVOLENY
                                                            </TranslateText>
                                                        );
                                                    return Promise.resolve();
                                                }
                                            })
                                        ]}
                                        disabled={displayOnly}
                                    />
                                    <Row>
                                        <Form.Item
                                            style={{
                                                marginBottom: 8,
                                                marginLeft: 8,
                                                marginRight: 40,
                                                fontWeight: 600
                                            }}
                                            name="zahremitent"
                                            label={
                                                <TranslateText>ISIN_EMISE_DLUHOPIS_ZAHRANICNI_EMITENT</TranslateText>
                                            }
                                            initialValue={
                                                udajeEmise?.zahremitent === true ||
                                                (udajeEmise?.zahremitent as any) === 'true' ||
                                                false
                                            }
                                            valuePropName="checked"
                                        >
                                            <Checkbox
                                                style={{
                                                    position: 'relative',
                                                    top: -2,
                                                    left: 4
                                                }}
                                                disabled={displayOnly}
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            style={{
                                                marginBottom: 8,
                                                marginRight: 40,
                                                fontWeight: 600
                                            }}
                                            name="prioritni"
                                            label={<TranslateText>ISIN_EMISE_DLUHOPIS_PRIORITNI</TranslateText>}
                                            initialValue={
                                                udajeEmise?.prioritni === true ||
                                                (udajeEmise?.prioritni as any) === 'true' ||
                                                false
                                            }
                                            valuePropName="checked"
                                        >
                                            <Checkbox
                                                style={{
                                                    position: 'relative',
                                                    top: -2,
                                                    left: 4
                                                }}
                                                disabled={displayOnly}
                                                onClick={(ev: any) => {
                                                    rerender();
                                                    if (ev.target.checked && !form.getFieldValue('perpetuitni')) {
                                                        form.setFieldsValue({
                                                            druhdluhopiscfi: EDruhDluhopisu.VD
                                                        });
                                                    }
                                                }}
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            style={{
                                                marginBottom: 8,
                                                marginRight: 40,
                                                fontWeight: 600
                                            }}
                                            name="podrizeny"
                                            label={<TranslateText>ISIN_EMISE_DLUHOPIS_PODRIZENY</TranslateText>}
                                            initialValue={
                                                udajeEmise?.podrizeny === true ||
                                                (udajeEmise?.podrizeny as any) === 'true' ||
                                                false
                                            }
                                            valuePropName="checked"
                                        >
                                            <Checkbox
                                                style={{
                                                    position: 'relative',
                                                    top: -2,
                                                    left: 4
                                                }}
                                                disabled={displayOnly}
                                                onClick={() => {
                                                    form.setFieldsValue({
                                                        zaruceniemise: undefined
                                                    });
                                                    rerender();
                                                }}
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            style={{
                                                marginBottom: 8,
                                                fontWeight: 600
                                            }}
                                            name="vymenitelny"
                                            label={<TranslateText>ISIN_EMISE_DLUHOPIS_VYMENITELNY</TranslateText>}
                                            initialValue={
                                                udajeEmise?.vymenitelny === true ||
                                                (udajeEmise?.vymenitelny as any) === 'true' ||
                                                false
                                            }
                                            valuePropName="checked"
                                        >
                                            <Checkbox
                                                style={{
                                                    position: 'relative',
                                                    top: -2,
                                                    left: 4
                                                }}
                                                onClick={(ev: any) => {
                                                    if (
                                                        ev.target.checked &&
                                                        form.getFieldValue('druhdluhopisu') !== EDruhDluhopisu.ZL &&
                                                        form.getFieldValue('druhdluhopisu') !== EDruhDluhopisu.KO &&
                                                        !form.getFieldValue('perpetuitni')
                                                    ) {
                                                        form.setFieldsValue({
                                                            druhdluhopiscfi: EDruhDluhopisu.VD
                                                        });
                                                    }
                                                    rerender();
                                                }}
                                                disabled={displayOnly}
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            style={{
                                                marginBottom: 8,
                                                fontWeight: 600
                                            }}
                                            name="perpetuitni"
                                            label={<TranslateText>ISIN_EMISE_DLUHOPIS_PERPETUITNI</TranslateText>}
                                            initialValue={
                                                udajeEmise?.perpetuitni === true ||
                                                (udajeEmise?.perpetuitni as any) === 'true' ||
                                                false
                                            }
                                            valuePropName="checked"
                                        >
                                            <Checkbox
                                                onClick={(ev: any) => {
                                                    if (ev.target.checked) {
                                                        form.setFieldsValue({
                                                            podminkysdatumem: 'false',
                                                            emisnipodminky: undefined,
                                                            druhdluhopiscfi: EDruhDluhopisu.OD
                                                        });
                                                    } else {
                                                        form.setFieldsValue({
                                                            podminkysdatumem: 'true',
                                                            emisnipodminky: undefined,
                                                            druhdluhopiscfi:
                                                                form.getFieldValue('vymenitelny') ||
                                                                form.getFieldValue('prioritni')
                                                                    ? EDruhDluhopisu.VD
                                                                    : undefined
                                                        });
                                                    }
                                                    rerender();
                                                }}
                                                style={{
                                                    position: 'relative',
                                                    top: -2,
                                                    left: 4
                                                }}
                                                disabled={displayOnly}
                                            />
                                        </Form.Item>
                                    </Row>
                                    <SelectComponent
                                        name="druhdluhopiscfi"
                                        defaultValue={udajeEmise?.druhdluhopiscfi}
                                        disabled={
                                            form.getFieldValue('druhdluhopisu') === EDruhDluhopisu.ZL ||
                                            form.getFieldValue('druhdluhopisu') === EDruhDluhopisu.KO ||
                                            form.getFieldValue('vymenitelny') ||
                                            form.getFieldValue('prioritni') ||
                                            form.getFieldValue('perpetuitni') ||
                                            displayOnly
                                        }
                                        onChange={val => {
                                            val === EDruhDluhopisu.VD &&
                                                form.setFieldsValue({
                                                    vymenitelny: true
                                                });
                                            rerender();
                                        }}
                                        labelParagraph="ISIN_EMISE_DLUHOPIS_TYP"
                                        isFormItem={true}
                                        data={druhDluhopisCfiSelect}
                                    />
                                </>
                            )}
                            {druhCP === EDruhCp.WARRANT && (
                                <>
                                    <SelectComponent
                                        isFormItem={true}
                                        defaultValue={udajeEmise?.podkladaktivum}
                                        name="podkladaktivum"
                                        labelParagraph="ISIN_EMISE_WARRANT_PODKLAD"
                                        data={podkladAktivumSelect}
                                        disabled={displayOnly}
                                    />
                                    {renderPodkladAktiva()}
                                    <SelectComponent
                                        isFormItem={true}
                                        name="typwarrantu"
                                        defaultValue={udajeEmise?.typwarrantu}
                                        infoTooltipText={!displayOnly ? 'ISIN_EMISE_WARRANT_TYP_HELP' : undefined}
                                        labelParagraph="ISIN_EMISE_WARRANT_TYP"
                                        data={typWarrantuSelect}
                                        disabled={displayOnly}
                                    />

                                    <SelectComponent
                                        isFormItem={true}
                                        name="warrnakupprodej"
                                        defaultValue={udajeEmise?.warrnakupprodej}
                                        labelParagraph="ISIN_EMISE_WARRANT_WARR"
                                        data={warrNakupProdejSelect}
                                        disabled={displayOnly}
                                    />

                                    <SelectComponent
                                        isFormItem={true}
                                        name="uplatneniopce"
                                        defaultValue={udajeEmise?.uplatneniopce}
                                        labelParagraph="ISIN_EMISE_WARRANT_OPCE"
                                        data={warrantOpceSelect}
                                        disabled={displayOnly}
                                    />
                                </>
                            )}
                            {zmenaIsin && udajeEmise?.podobacp === EEmitentZadaOpridISIN.LISTINNY && (
                                <SelectComponent
                                    name="podobacp"
                                    labelParagraph={'ISIN_EMISE_AKCIE_PODOBA'}
                                    infoTooltipText="ISIN_ROZCESTNIK_HELP_ZMENA"
                                    isFormItem={true}
                                    onChange={() => {
                                        /*  form.setFieldsValue({
                                            evidencecp: EEvidenceCp.JINY
                                        }); */
                                        rerender();
                                    }}
                                    data={[
                                        {
                                            value: EEmitentZadaOpridISIN.LISTINNY,
                                            labelcz: 'ISIN_ROZCESTNIK_PRIDELENI_ISIN_LIST'
                                        },
                                        {
                                            value: EEmitentZadaOpridISIN.ZAKNIHOVANY,
                                            labelcz: 'ISIN_ROZCESTNIK_PRIDELENI_ISIN_ZAKN'
                                        }
                                    ]}
                                    defaultValue={identifikacePutData?.podobacp || rozcestnikInput?.podobacp}
                                    disabled={displayOnly}
                                    // disabled={form.getFieldValue('evidencecp') === EEvidenceCp.CDCPDLT}
                                />
                            )}
                            {(rozcestnikInput?.evidencecp ||
                                udajeEmise?.evidencecp ||
                                form.getFieldValue('podobacp') === EEmitentZadaOpridISIN.ZAKNIHOVANY) && (
                                <SelectComponent
                                    name="evidencecp"
                                    defaultValue={rozcestnikInput?.evidencecp || udajeEmise?.evidencecp}
                                    labelParagraph="ISIN_ROZCESTNIK_PRIDELENI_OSOBA"
                                    isFormItem={true}
                                    disabled={
                                        displayOnly ||
                                        (zmenaIsin
                                            ? (rozcestnikInput?.evidencecp || udajeEmise?.evidencecp) ===
                                                  EEvidenceCp.CDCP ||
                                              (rozcestnikInput?.evidencecp || udajeEmise?.evidencecp) ===
                                                  EEvidenceCp.CDCPDLT
                                            : true)
                                    }
                                    data={
                                        zmenaIsin &&
                                        (rozcestnikInput?.evidencecp || udajeEmise?.evidencecp) !== EEvidenceCp.CDCP &&
                                        (rozcestnikInput?.evidencecp || udajeEmise?.evidencecp) !==
                                            EEvidenceCp.CDCPDLT &&
                                        (rozcestnikInput?.evidencecp || udajeEmise?.evidencecp) !== EEvidenceCp.JINY
                                            ? evidenceCpSelectEditIsin
                                            : evidenceCpSelect
                                    }
                                    notRequired={!zmenaIsin}
                                    onChange={rerender}
                                />
                            )}

                            {form.getFieldValue('evidencecp') === EEvidenceCp.JINY && (
                                <>
                                    <InputComponent
                                        label="ISIN_ROZCESTNIK_PRIDELENI_OSOBA_JINY_ICOLEI"
                                        name="jinaosobaid"
                                        initialValue={udajeEmise?.jinaosobaid}
                                        type="text"
                                        maxLength={20}
                                        rules={[
                                            () => ({
                                                async validator(_rule: RuleObject, value: string) {
                                                    if (validateLEI(value) === 0 || validateICO(value) === 0)
                                                        return Promise.resolve();
                                                    return Promise.reject(
                                                        <TranslateText>
                                                            {value.length > 8 ? 'NEVALIDNI_LEI' : 'NEVALIDNI_ICO'}
                                                        </TranslateText>
                                                    );
                                                }
                                            })
                                        ]}
                                        disabled={displayOnly}
                                    />
                                    <InputComponent
                                        label="ISIN_ROZCESTNIK_PRIDELENI_OSOBA_JINY"
                                        name="jinaosoba"
                                        initialValue={udajeEmise?.jinaosoba}
                                        type="text"
                                        maxLength={500}
                                        disabled={displayOnly}
                                    />
                                </>
                            )}

                            {druhCP !== EDruhCp.NASTROJKOLINV && (
                                <InputNumberComponent
                                    name="mtno"
                                    label="ISIN_EMISE_AKCIE_HODNOTA_ZA_CP"
                                    infoTooltipText={
                                        druhCP === EDruhCp.INVCERT && !displayOnly
                                            ? 'ISIN_EMISE_CERTIFIKAT_CP_HELP'
                                            : undefined
                                    }
                                    onChangeCustom={onCelkovaHodnotaEmiseChange}
                                    maxLength={16}
                                    precision={2}
                                    rules={dveDesetinneMistaValidation(
                                        form.getFieldValue('druhdluhopisu') === EDruhDluhopisu.OK ||
                                            form.getFieldValue('druhakcie') === EDruhAkcie.KUS ||
                                            form.getFieldValue('druhakcie') === EDruhAkcie.PRIORKUS ||
                                            form.getFieldValue('druhakcie') === EDruhAkcie.JINA ||
                                            druhCP === EDruhCp.INVCERT ||
                                            druhCP === EDruhCp.PODILLIST
                                            ? false
                                            : true
                                    )}
                                    initialValue={
                                        udajeEmise?.mtno ||
                                        (form.getFieldValue('druhakcie') === EDruhAkcie.PRIORKUS ||
                                        form.getFieldValue('druhakcie') === EDruhAkcie.KUS
                                            ? 0
                                            : undefined)
                                    }
                                    disabled={
                                        form.getFieldValue('druhakcie') === EDruhAkcie.PRIORKUS ||
                                        form.getFieldValue('druhakcie') === EDruhAkcie.KUS ||
                                        displayOnly
                                    }
                                />
                            )}

                            {druhCP !== EDruhCp.NASTROJKOLINV && (
                                <SelectComponent
                                    isFormItem={true}
                                    name="dnom"
                                    defaultValue={udajeEmise?.dnom}
                                    labelParagraph="MENA_JMENOVITEHODNOTY"
                                    data={cisMeny}
                                    disabled={displayOnly}
                                />
                            )}

                            {druhCP !== EDruhCp.NASTROJKOLINV && (
                                <InputNumberComponent
                                    name="nbti"
                                    label="ISIN_EMISE_AKCIE_KUSU"
                                    infoTooltipText={
                                        druhCP === EDruhCp.DLUHOPIS && !displayOnly
                                            ? form.getFieldValue('druhdluhopisu') === EDruhDluhopisu.OK ||
                                              form.getFieldValue('druhdluhopisu') === EDruhDluhopisu.OJ
                                                ? 'ISIN_EMISE_AKCIE_KUSU_HELP'
                                                : undefined
                                            : undefined
                                    }
                                    rules={numberValidation(druhCP === EDruhCp.PODILLIST ? false : true)}
                                    initialValue={udajeEmise?.nbti}
                                    precision={0}
                                    maxLength={20}
                                    onChangeCustom={() => {
                                        onCelkovaHodnotaEmiseChange();
                                    }}
                                    disabled={displayOnly}
                                />
                            )}

                            {druhCP !== EDruhCp.NASTROJKOLINV && (
                                <InputComponent
                                    name="ignore2"
                                    label="ISIN_EMISE_AKCIE_CELKOVA_HODNOTA"
                                    disabled={true}
                                    notRequired={true}
                                />
                            )}
                            {(rozcestnikInput?.podobacp || udajeEmise?.podobacp) &&
                                (!zmenaIsin || udajeEmise?.podobacp !== 'LIST') && (
                                    <InputComponent
                                        name="ignore3"
                                        label="ISIN_EMISE_AKCIE_PODOBA"
                                        disabled={true}
                                        defaultValue={
                                            getLanguageObject(language)[
                                                `ISIN_ROZCESTNIK_PRIDELENI_ISIN_${(rozcestnikInput?.podobacp || udajeEmise?.podobacp)!.toUpperCase()}`
                                            ]
                                        }
                                        notRequired={true}
                                    />
                                )}

                            <SelectComponent
                                name="formacp"
                                defaultValue={udajeEmise?.formacp}
                                isFormItem={true}
                                infoTooltipText={druhCP !== EDruhCp.AKCIE ? 'ISIN_EMISE_NON_AKCIE_FORMA_HELP' : ''}
                                labelParagraph="ISIN_EMISE_AKCIE_FORMA"
                                onChange={() => rerender()}
                                data={druhCP === EDruhCp.AKCIE ? formaCpSelectAkcie : formaCpSelect}
                                disabled={displayOnly}
                            />
                            <SelectComponent
                                name="ompr"
                                defaultValue={
                                    typeof udajeEmise?.ompr === 'boolean' ? String(udajeEmise.ompr) : udajeEmise?.ompr
                                }
                                isFormItem={true}
                                labelParagraph="ISIN_EMISE_AKCIE_PREVODITELNOST"
                                infoTooltipText={
                                    druhCP === EDruhCp.AKCIE && !displayOnly
                                        ? 'ISIN_EMISE_AKCIE_PREVODITELNOST_TOOLTIP'
                                        : ''
                                }
                                data={trueFalseSelect}
                                onChange={rerender}
                                disabled={displayOnly}
                            />

                            {form.getFieldValue('ompr') === 'true' &&
                                (form.getFieldValue('evidencecp') === EEvidenceCp.CDCP ||
                                    form.getFieldValue('evidencecp') === EEvidenceCp.CDCPDLT) && (
                                    <InputComponent
                                        label="ISIN_EMISE_AKCIE_PREVODITELNOST_POPIS"
                                        name="omprpopis"
                                        initialValue={udajeEmise?.omprpopis}
                                        maxLength={500}
                                        rules={[
                                            () => ({
                                                validator(_, value: string) {
                                                    if (!value || globalInputRegExp.test(value))
                                                        return Promise.resolve();
                                                    return Promise.reject(
                                                        <TranslateText>VALIDACE_GLOBAL_INPUT</TranslateText>
                                                    );
                                                }
                                            })
                                        ]}
                                        disabled={displayOnly}
                                    />
                                )}
                            {(druhCP === EDruhCp.AKCIE || druhCP === EDruhCp.NASTROJKOLINV) &&
                                (displayOnly ? !!udajeEmise?.hlprava : true) && (
                                    <>
                                        <SelectComponent
                                            isFormItem={true}
                                            name="hlprava"
                                            defaultValue={udajeEmise?.hlprava}
                                            labelParagraph="ISIN_EMISE_AKCIE_HLASOVACI"
                                            data={hlasovaciPravaSelect}
                                            notRequired={
                                                druhCP === EDruhCp.AKCIE &&
                                                form.getFieldValue('druhakcie') === EDruhAkcie.JINA
                                            }
                                            disabled={displayOnly}
                                        />
                                    </>
                                )}

                            {druhCP === EDruhCp.INVCERT && (
                                <>
                                    <SelectComponent
                                        isFormItem={true}
                                        name="druhcertifikatu"
                                        defaultValue={udajeEmise?.druhcertifikatu}
                                        labelParagraph="ISIN_EMISE_CERTIFIKAT_DRUH"
                                        data={druhCertifikatuSelect}
                                        onChange={val => {
                                            form.setFieldsValue({
                                                vymennypomer: val === EDruhCertifikatu.NEPAKOVY ? 0 : undefined
                                            });
                                            rerender();
                                        }}
                                        disabled={displayOnly}
                                    />
                                    {form.getFieldValue('druhcertifikatu') === EDruhCertifikatu.NEPAKOVY && (
                                        <>
                                            <SelectComponent
                                                isFormItem={true}
                                                name="typcertifikatu"
                                                defaultValue={udajeEmise?.typcertifikatu}
                                                labelParagraph="ISIN_EMISE_CERTIFIKAT_TYP"
                                                data={typCertifikatuSelect}
                                                disabled={displayOnly}
                                            />

                                            <SelectComponent
                                                isFormItem={true}
                                                name="vyplaty"
                                                defaultValue={udajeEmise?.vyplaty}
                                                labelParagraph="ISIN_EMISE_CERTIFIKAT_VYPLATY"
                                                data={vyplatySelect}
                                                disabled={displayOnly}
                                            />
                                            <SelectComponent
                                                isFormItem={true}
                                                name="splaceni"
                                                defaultValue={udajeEmise?.splaceni}
                                                labelParagraph="ISIN_EMISE_CERTIFIKAT_SPLACENI"
                                                data={splaceniSelect}
                                                onChange={() => rerender()}
                                                disabled={displayOnly}
                                            />
                                            {form.getFieldValue('splaceni') === ESplaceni.H && (
                                                <SelectComponent
                                                    isFormItem={true}
                                                    name="menavyporadani"
                                                    defaultValue={udajeEmise?.menavyporadani}
                                                    labelParagraph="MENA_VYPORADANI"
                                                    data={cisMeny}
                                                    disabled={displayOnly}
                                                />
                                            )}
                                        </>
                                    )}
                                    {form.getFieldValue('druhcertifikatu') && (
                                        <>
                                            <SelectComponent
                                                isFormItem={true}
                                                name="podkladaktivita"
                                                defaultValue={udajeEmise?.podkladaktivita}
                                                labelParagraph="ISIN_EMISE_CERTIFIKAT_AKTIVA"
                                                data={
                                                    form.getFieldValue('druhcertifikatu') === EDruhCertifikatu.PAKOVY
                                                        ? certifikatAktivitaAltSelect
                                                        : certifikatAktivitaSelect
                                                }
                                                disabled={displayOnly}
                                            />
                                        </>
                                    )}
                                    {form.getFieldValue('druhcertifikatu') === EDruhCertifikatu.PAKOVY && (
                                        <>
                                            <SelectComponent
                                                isFormItem={true}
                                                name="typzavnabariere"
                                                defaultValue={udajeEmise?.typzavnabariere}
                                                labelParagraph="ISIN_EMISE_CERTIFIKAT_TYP_ZAV"
                                                data={typZavislostiSelect}
                                                disabled={displayOnly}
                                            />

                                            <SelectComponent
                                                isFormItem={true}
                                                name="dlouhekratke"
                                                defaultValue={udajeEmise?.dlouhekratke}
                                                labelParagraph="ISIN_EMISE_CERTIFIKAT_DK"
                                                data={dlouheKratkeSelect}
                                                disabled={displayOnly}
                                            />

                                            <SelectComponent
                                                isFormItem={true}
                                                name="druhuplatneni"
                                                defaultValue={udajeEmise?.druhuplatneni}
                                                labelParagraph="ISIN_EMISE_CERTIFIKAT_OPCE"
                                                data={druhUplatneniSelect}
                                                disabled={displayOnly}
                                            />
                                        </>
                                    )}
                                    <SelectComponent
                                        isFormItem={true}
                                        name="urokovasazba"
                                        defaultValue={udajeEmise?.urokovasazba}
                                        labelParagraph="ISIN_EMISE_DLUHOPIS_SAZBA"
                                        data={urokovaSazbaDluhopis}
                                        onChange={val => {
                                            val === EUrokovaSazba.Z
                                                ? form.setFieldsValue({
                                                      frekvencevyplaty: EFrekvenceVyplaty.NA
                                                  })
                                                : /* form.getFieldValue('frekvencevyplaty') === EFrekvenceVyplaty.NA &&
                                                  form.setFieldsValue({
                                                      frekvencevyplaty: undefined
                                                  })*/ null;

                                            rerender();
                                        }}
                                        disabled={displayOnly}
                                    />
                                    {form.getFieldValue('urokovasazba') === EUrokovaSazba.J && (
                                        <InputComponent
                                            label="POPIS"
                                            name="urokovasazbapopis"
                                            initialValue={udajeEmise?.urokovasazbapopis}
                                            maxLength={500}
                                            rules={[
                                                () => ({
                                                    validator(_, value: string) {
                                                        if (!value || globalInputRegExp.test(value))
                                                            return Promise.resolve();
                                                        return Promise.reject(
                                                            <TranslateText>VALIDACE_GLOBAL_INPUT</TranslateText>
                                                        );
                                                    }
                                                })
                                            ]}
                                            disabled={displayOnly}
                                        />
                                    )}

                                    {form.getFieldValue('urokovasazba') === EUrokovaSazba.F && (
                                        <InputNumberComponent
                                            label="ISIN_EMISE_DLUHOPIS_SAZBA_VYSE"
                                            infoTooltipText={
                                                !displayOnly &&
                                                (form.getFieldValue('druhdluhopisu') === EDruhDluhopisu.OK ||
                                                    form.getFieldValue('druhdluhopisu') === EDruhDluhopisu.OJ)
                                                    ? 'ISIN_EMISE_DLUHOPIS_SAZBA_HELP'
                                                    : undefined
                                            }
                                            name="fixnisazba"
                                            rules={procentaValidation6(true)}
                                            initialValue={udajeEmise?.fixnisazba}
                                            maxLength={9}
                                            precision={6}
                                            disabled={displayOnly}
                                        />
                                    )}
                                    {form.getFieldValue('evidencecp') === EEvidenceCp.CDCP && renderPodkladAktiva()}
                                    {form.getFieldValue('evidencecp') === EEvidenceCp.CDCP &&
                                        form.getFieldValue('druhcertifikatu') && (
                                            <InputNumberComponent
                                                label="VYMENNY_POMER"
                                                name="vymennypomer"
                                                rules={dveDesetinneMistaValidation(
                                                    form.getFieldValue('druhcertifikatu') === 'PAKOVY'
                                                )}
                                                disabled={
                                                    (form.getFieldValue('druhcertifikatu') &&
                                                        form.getFieldValue('druhcertifikatu') !== 'PAKOVY') ||
                                                    displayOnly
                                                }
                                                initialValue={
                                                    form.getFieldValue('druhcertifikatu') === 'PAKOVY'
                                                        ? udajeEmise?.vymennypomer
                                                        : 0
                                                }
                                                precision={6}
                                            />
                                        )}
                                    {form.getFieldValue('druhcertifikatu') === 'PAKOVY' && (
                                        <>
                                            <InputNumberComponent
                                                label="STRIKE_CENA"
                                                name="strikecena"
                                                rules={dveDesetinneMistaValidation()}
                                                initialValue={udajeEmise?.strikecena}
                                                disabled={displayOnly}
                                            />
                                            <SelectComponent
                                                isFormItem={true}
                                                name="strikemena"
                                                defaultValue={udajeEmise?.strikemena}
                                                labelParagraph="STRIKE_MENA"
                                                data={cisMeny}
                                                disabled={displayOnly}
                                            />
                                        </>
                                    )}
                                    <SelectComponent
                                        isFormItem={true}
                                        name="podminkysdatumem"
                                        defaultValue={
                                            typeof udajeEmise?.podminkysdatumem === 'boolean'
                                                ? String(udajeEmise.podminkysdatumem)
                                                : udajeEmise?.podminkysdatumem
                                        }
                                        labelParagraph="ISIN_EMISE_DLUHOPIS_EMISNI_PODMINKY_DATUM"
                                        data={trueFalseSelect}
                                        onChange={() => rerender()}
                                        disabled={displayOnly}
                                    />
                                    {form.getFieldValue('podminkysdatumem') === ETrueFalse.TRUE && (
                                        <DatePickerComponent
                                            name="datumsplatnosti"
                                            label="ISIN_EMISE_DLUHOPIS_SPLATNOST"
                                            initialValue={
                                                udajeEmise?.datumsplatnosti
                                                    ? dayjs(udajeEmise.datumsplatnosti)
                                                    : undefined
                                            }
                                            disabledDate={current => current < dayjs()}
                                            onChange={() => rerender()}
                                            disabled={displayOnly}
                                        />
                                    )}
                                    <SelectComponent
                                        isFormItem={true}
                                        name="predcassplatnost"
                                        defaultValue={
                                            typeof udajeEmise?.predcassplatnost === 'boolean'
                                                ? String(udajeEmise.predcassplatnost)
                                                : udajeEmise?.predcassplatnost
                                        }
                                        labelParagraph="ISIN_EMISE_CERTIFIKAT_SPLATNOST"
                                        data={trueFalseSelect}
                                        onChange={() => rerender()}
                                        disabled={displayOnly}
                                    />

                                    <SelectComponent
                                        isFormItem={true}
                                        name="zaruceniemisewar"
                                        defaultValue={udajeEmise?.zaruceniemisewar}
                                        labelParagraph="ISIN_EMISE_WARRANT_ZARUCENI"
                                        data={zarucenieEmiseSelect}
                                        infoTooltipText={
                                            !displayOnly &&
                                            (form.getFieldValue('druhdluhopisu') === EDruhDluhopisu.OK ||
                                                form.getFieldValue('druhdluhopisu') === EDruhDluhopisu.OJ)
                                                ? 'ISIN_EMISE_DLUHOPIS_ODDELENI_KUPONU_HELP'
                                                : undefined
                                        }
                                        disabled={displayOnly}
                                    />
                                    <SelectComponent
                                        isFormItem={true}
                                        name="frekvencevyplaty"
                                        defaultValue={udajeEmise?.frekvencevyplaty}
                                        labelParagraph="ISIN_EMISE_DLUHOPIS_FREKVENCE"
                                        data={frekvenceVyplatySelect}
                                        disabled={form.getFieldValue('urokovasazba') === EUrokovaSazba.Z || displayOnly}
                                    />
                                </>
                            )}

                            {druhCP === EDruhCp.DLUHOPIS && (
                                <>
                                    <SelectComponent
                                        isFormItem={true}
                                        name="urokovasazba"
                                        defaultValue={udajeEmise?.urokovasazba}
                                        labelParagraph="ISIN_EMISE_DLUHOPIS_SAZBA"
                                        data={urokovaSazbaDluhopis}
                                        onChange={val => {
                                            val === EUrokovaSazba.Z
                                                ? form.setFieldsValue({
                                                      frekvencevyplaty: EFrekvenceVyplaty.NA
                                                  })
                                                : /* form.getFieldValue('frekvencevyplaty') === EFrekvenceVyplaty.NA &&
                                                  form.setFieldsValue({
                                                      frekvencevyplaty: undefined
                                                  })*/ null;

                                            rerender();
                                        }}
                                        disabled={displayOnly}
                                    />
                                    {form.getFieldValue('urokovasazba') === EUrokovaSazba.J && (
                                        <InputComponent
                                            label="POPIS"
                                            name="urokovasazbapopis"
                                            initialValue={udajeEmise?.urokovasazbapopis}
                                            maxLength={500}
                                            rules={[
                                                () => ({
                                                    validator(_, value: string) {
                                                        if (!value || globalInputRegExp.test(value))
                                                            return Promise.resolve();
                                                        return Promise.reject(
                                                            <TranslateText>VALIDACE_GLOBAL_INPUT</TranslateText>
                                                        );
                                                    }
                                                })
                                            ]}
                                            disabled={displayOnly}
                                        />
                                    )}

                                    {form.getFieldValue('urokovasazba') === EUrokovaSazba.F && (
                                        <InputNumberComponent
                                            label="ISIN_EMISE_DLUHOPIS_SAZBA_VYSE"
                                            infoTooltipText={
                                                !displayOnly &&
                                                (form.getFieldValue('druhdluhopisu') === EDruhDluhopisu.OK ||
                                                    form.getFieldValue('druhdluhopisu') === EDruhDluhopisu.OJ)
                                                    ? 'ISIN_EMISE_DLUHOPIS_SAZBA_HELP'
                                                    : undefined
                                            }
                                            name="fixnisazba"
                                            rules={procentaValidation6(true)}
                                            initialValue={udajeEmise?.fixnisazba}
                                            maxLength={9}
                                            precision={6}
                                            disabled={displayOnly}
                                        />
                                    )}
                                    {(form.getFieldValue('evidencecp') === EEvidenceCp.CDCPDLT ||
                                        form.getFieldValue('evidencecp') === EEvidenceCp.CDCP) && (
                                        <>
                                            <SelectComponent
                                                isFormItem={true}
                                                name="alikvynos"
                                                defaultValue={udajeEmise?.alikvynos}
                                                labelParagraph="ISIN_EMISE_WARRANT_ALIKVYNOS"
                                                data={
                                                    form.getFieldValue('urokovasazba') === EUrokovaSazba.Z
                                                        ? alikVynosSelectZero
                                                        : alikVynosSelect
                                                }
                                                onChange={rerender}
                                                disabled={displayOnly}
                                            />
                                            {form.getFieldValue('alikvynos') === ECisalikvynos._13 && (
                                                <>
                                                    <InputComponent
                                                        infoTooltipText={
                                                            !displayOnly ? 'ISIN_EMISE_WARRANT_JINA_TEXT' : undefined
                                                        }
                                                        label="ISIN_EMISE_CERTIFIKAT_SAZBA_POPISTE"
                                                        name="alikvynospopis"
                                                        initialValue={udajeEmise?.alikvynospopis}
                                                        maxLength={500}
                                                        disabled={displayOnly}
                                                        rules={[
                                                            () => ({
                                                                validator(_, value: string) {
                                                                    if (!value || globalInputRegExp.test(value))
                                                                        return Promise.resolve();
                                                                    return Promise.reject(
                                                                        <TranslateText>
                                                                            VALIDACE_GLOBAL_INPUT
                                                                        </TranslateText>
                                                                    );
                                                                }
                                                            })
                                                        ]}
                                                    />
                                                </>
                                            )}
                                        </>
                                    )}
                                    <SelectComponent
                                        isFormItem={true}
                                        name="podminkysdatumem"
                                        disabled={true}
                                        defaultValue={
                                            form.getFieldValue('perpetuitni')
                                                ? String(form.getFieldValue('perpetuitni'))
                                                : typeof udajeEmise?.podminkysdatumem === 'boolean'
                                                  ? String(udajeEmise?.podminkysdatumem)
                                                  : udajeEmise?.podminkysdatumem ||
                                                      udajeEmise?.perpetuitni === 'boolean'
                                                    ? String(!udajeEmise?.perpetuitni)
                                                    : 'true'
                                        }
                                        labelParagraph="ISIN_EMISE_DLUHOPIS_EMISNI_PODMINKY_DATUM"
                                        data={[
                                            {
                                                value: ETrueFalse.TRUE,
                                                labelcz: 'ANO'
                                            },
                                            {
                                                value: ETrueFalse.FALSE,
                                                labelcz: 'ISIN_EMISE_NE'
                                            }
                                        ]}
                                        rules={[
                                            {
                                                required: true,
                                                message: <TranslateText>NEVALIDNI_POVINNA_POLOZKA</TranslateText>
                                            },
                                            () => ({
                                                validator(_, value: string) {
                                                    if (
                                                        (value === ETrueFalse.FALSE &&
                                                            form.getFieldValue('druhdluhopiscfi') !==
                                                                EDruhDluhopisu.OD) ||
                                                        (value === ETrueFalse.TRUE &&
                                                            form.getFieldValue('druhdluhopiscfi') === EDruhDluhopisu.OD)
                                                    )
                                                        return Promise.reject(
                                                            <TranslateText>
                                                                ISIN_EMISE_DLUHOPIS_TYP_NEVHODNE_ZVOLENY
                                                            </TranslateText>
                                                        );
                                                    return Promise.resolve();
                                                }
                                            })
                                        ]}
                                        onChange={() => rerender()}
                                    />
                                    {form.getFieldValue('podminkysdatumem') === ETrueFalse.TRUE &&
                                        form.getFieldValue('druhdluhopiscfi') !== EDruhDluhopisu.OD && (
                                            <DatePickerComponent
                                                infoTooltipText={
                                                    !displayOnly &&
                                                    (form.getFieldValue('druhdluhopisu') === EDruhDluhopisu.OK ||
                                                        form.getFieldValue('druhdluhopisu') === EDruhDluhopisu.OJ)
                                                        ? 'ISIN_EMISE_DLUHOPIS_SPLATNOST_HELP2'
                                                        : undefined
                                                }
                                                name="datumsplatnosti"
                                                label="ISIN_EMISE_DLUHOPIS_SPLATNOST"
                                                initialValue={
                                                    udajeEmise?.datumsplatnosti
                                                        ? dayjs(udajeEmise.datumsplatnosti)
                                                        : undefined
                                                }
                                                disabledDate={current => current < dayjs()}
                                                disabled={displayOnly}
                                            />
                                        )}

                                    {/* <SelectComponent
                                        isFormItem={true}
                                        name="oddelenikuponu"
                                        defaultValue={
                                            typeof udajeEmise?.oddelenikuponu === 'boolean'
                                                ? String(udajeEmise.oddelenikuponu)
                                                : undefined
                                        }
                                        infoTooltipText={
                                            druhCP === EDruhCp.DLUHOPIS
                                                ? form.getFieldValue('druhdluhopisu') === EDruhDluhopisu.OK ||
                                                  form.getFieldValue('druhdluhopisu') === EDruhDluhopisu.OJ
                                                    ? 'ISIN_EMISE_DLUHOPIS_ODDELENI_KUPONU_HELP'
                                                    : undefined
                                                : undefined
                                        }
                                        labelParagraph="ISIN_EMISE_DLUHOPIS_ODDELENI_KUPONU"
                                        data={trueFalseSelect}
                                        onChange={() => rerender()}
                                        disabled={displayOnly}
                                    /> */}

                                    {(form.getFieldValue('evidencecp') === EEvidenceCp.CDCP ||
                                        form.getFieldValue('evidencecp') === EEvidenceCp.CDCPDLT) &&
                                        form.getFieldValue('oddelenikuponu') === ETrueFalse.TRUE && (
                                            <>
                                                <SelectComponent
                                                    isFormItem={true}
                                                    name="pridelitnavse"
                                                    defaultValue={
                                                        typeof udajeEmise?.pridelitnavse === 'boolean'
                                                            ? String(udajeEmise.pridelitnavse)
                                                            : udajeEmise?.pridelitnavse
                                                    }
                                                    labelParagraph="ISIN_EMISE_DLUHOPIS_PRIDELIT_ISIN"
                                                    data={trueFalseSelect}
                                                    onChange={() => rerender()}
                                                    disabled={displayOnly}
                                                />
                                                {form.getFieldValue('pridelitnavse') === ETrueFalse.TRUE && (
                                                    <InputComponent
                                                        infoTooltipText={
                                                            !displayOnly &&
                                                            (form.getFieldValue('druhdluhopisu') ===
                                                                EDruhDluhopisu.OK ||
                                                                form.getFieldValue('druhdluhopisu') ===
                                                                    EDruhDluhopisu.OJ)
                                                                ? 'ISIN_EMISE_DLUHOPIS_PRIDELIT_ISIN_KUPONY_HELP'
                                                                : undefined
                                                        }
                                                        label="ISIN_EMISE_DLUHOPIS_PRIDELIT_ISIN_KUPONY"
                                                        name="pocetkuponu"
                                                        initialValue={udajeEmise?.pocetkuponu}
                                                        maxLength={20}
                                                        rules={numberValidation()}
                                                        disabled={displayOnly}
                                                    />
                                                )}
                                            </>
                                        )}
                                    {druhCP !== EDruhCp.DLUHOPIS &&
                                        (rozcestnikInput?.podobacp === EEmitentZadaOpridISIN.LISTINNY ||
                                            udajeEmise?.podobacp === EEmitentZadaOpridISIN.LISTINNY ||
                                            udajeEmise?.podobacp === EEmitentZadaOpridISIN.LISTSBER ||
                                            rozcestnikInput?.podobacp === EEmitentZadaOpridISIN.LISTSBER) && (
                                            <>
                                                <SelectComponent
                                                    isFormItem={true}
                                                    name="podminkysdatumem"
                                                    defaultValue={
                                                        typeof udajeEmise?.podminkysdatumem === 'boolean'
                                                            ? String(udajeEmise.podminkysdatumem)
                                                            : udajeEmise?.podminkysdatumem
                                                    }
                                                    labelParagraph="ISIN_EMISE_WARRANT_EMISNI_PODMINKY"
                                                    data={trueFalseSelect}
                                                    onChange={() => rerender()}
                                                    disabled={displayOnly}
                                                />

                                                {form.getFieldValue('podminkysdatumem') === ETrueFalse.TRUE && (
                                                    <DatePickerComponent
                                                        name="datumsplatnosti"
                                                        label="ISIN_EMISE_DLUHOPIS_SPLATNOST"
                                                        initialValue={
                                                            udajeEmise?.datumsplatnosti
                                                                ? dayjs(udajeEmise.datumsplatnosti)
                                                                : undefined
                                                        }
                                                        disabledDate={current => current < dayjs()}
                                                        onChange={() => rerender()}
                                                        disabled={displayOnly}
                                                    />
                                                )}
                                            </>
                                        )}
                                    <SelectComponent
                                        isFormItem={true}
                                        name="emisnipodminky"
                                        defaultValue={udajeEmise?.emisnipodminky}
                                        labelParagraph="ISIN_EMISE_DLUHOPIS_EMIS_PODM"
                                        data={
                                            !form.getFieldValue('perpetuitni') ||
                                            form.getFieldValue('perpetuitni') === ETrueFalse.FALSE
                                                ? emisniPodminkySelectUmoznuje
                                                : emisniPodminkySelectNeumoznuje
                                        }
                                        infoTooltipText={
                                            druhCP === EDruhCp.DLUHOPIS && !displayOnly
                                                ? form.getFieldValue('druhdluhopisu') === EDruhDluhopisu.OK ||
                                                  form.getFieldValue('druhdluhopisu') === EDruhDluhopisu.OJ
                                                    ? 'ISIN_EMISE_DLUHOPIS_ODDELENI_KUPONU_HELP'
                                                    : undefined
                                                : undefined
                                        }
                                        onChange={val => {
                                            form.setFieldsValue({
                                                doplninformace: undefined
                                            });
                                            (val === EEmisniPodminky.UPSUMJ ||
                                                val === EEmisniPodminky.UPEMPO ||
                                                val === EEmisniPodminky.UPDMPO ||
                                                val === EEmisniPodminky.UPEDPO) &&
                                                (form.getFieldValue('evidencecp') === EEvidenceCp.CDCP ||
                                                    form.getFieldValue('evidencecp') === EEvidenceCp.CDCPDLT) &&
                                                setPostupnesplaceniData([
                                                    {
                                                        datumsplatnosti: undefined,
                                                        mtno: undefined,
                                                        dnom: undefined
                                                    }
                                                ]);
                                            rerender();
                                        }}
                                        disabled={displayOnly}
                                    />
                                    {(form.getFieldValue('emisnipodminky') === EEmisniPodminky.UPSUMJ ||
                                        form.getFieldValue('emisnipodminky') === EEmisniPodminky.UPEMPO ||
                                        form.getFieldValue('emisnipodminky') === EEmisniPodminky.UPDMPO ||
                                        form.getFieldValue('emisnipodminky') === EEmisniPodminky.UPEDPO) &&
                                        (form.getFieldValue('evidencecp') === EEvidenceCp.CDCP ||
                                            form.getFieldValue('evidencecp') === EEvidenceCp.CDCPDLT) && (
                                            <>
                                                <Paragraph text="ISIN_EMISE_DLUHOPIS_EMIS_PODM_TEST" />

                                                {postupnesplaceniData?.map((item, i) => {
                                                    return (
                                                        <div key={i}>
                                                            <div>
                                                                {i === 0 ||
                                                                (i > 0 && postupnesplaceniData.length - 1 === i) ? (
                                                                    <Title
                                                                        level={5}
                                                                        text="ISIN_EMISE_DLUHOPIS_EMIS_PODM_UPSE"
                                                                        style={{
                                                                            display: 'inline-block'
                                                                        }}
                                                                        dividerTitle={true}
                                                                    >
                                                                        {i === 0 && (
                                                                            <ButtonComponent
                                                                                id="c2PlusAltBtn"
                                                                                htmlType="button"
                                                                                type="primary"
                                                                                style={{
                                                                                    display: 'inline-block',
                                                                                    width: 50
                                                                                }}
                                                                                icon={<PlusOutlined />}
                                                                                onClick={() =>
                                                                                    setPostupnesplaceniData(pv => {
                                                                                        if (pv) {
                                                                                            const newState = [...pv];
                                                                                            newState.push({
                                                                                                datumsplatnosti:
                                                                                                    undefined,
                                                                                                mtno: undefined,
                                                                                                dnom: undefined
                                                                                            });
                                                                                            form.resetFields([
                                                                                                `mtno${pv.length}`,
                                                                                                `dnom${pv.length}`,
                                                                                                `datumsplatnosti${pv.length}`
                                                                                            ]);
                                                                                            return newState;
                                                                                        }
                                                                                        return [{}];
                                                                                    })
                                                                                }
                                                                                disabled={displayOnly}
                                                                            />
                                                                        )}
                                                                        {i > 0 &&
                                                                            postupnesplaceniData.length - 1 === i && (
                                                                                <ButtonComponent
                                                                                    id="c2MinusBtn"
                                                                                    htmlType="button"
                                                                                    type="primary"
                                                                                    style={{
                                                                                        display: 'inline-block',
                                                                                        width: 50
                                                                                    }}
                                                                                    icon={<MinusOutlined />}
                                                                                    onClick={() =>
                                                                                        setPostupnesplaceniData(pv => {
                                                                                            if (pv) {
                                                                                                form.resetFields([
                                                                                                    `mtno${i}`,
                                                                                                    `dnom${i}`,
                                                                                                    `datumsplatnosti${i}`
                                                                                                ]);
                                                                                                const newState = [
                                                                                                    ...pv
                                                                                                ];
                                                                                                newState.splice(i, 1);
                                                                                                return newState;
                                                                                            }
                                                                                            return [{}];
                                                                                        })
                                                                                    }
                                                                                    disabled={displayOnly}
                                                                                />
                                                                            )}
                                                                    </Title>
                                                                ) : (
                                                                    <Title
                                                                        level={5}
                                                                        text="ISIN_EMISE_DLUHOPIS_EMIS_PODM_UPSE"
                                                                        style={{
                                                                            display: 'inline-block'
                                                                        }}
                                                                        dividerTitle={true}
                                                                    />
                                                                )}
                                                            </div>
                                                            <InputNumberComponent
                                                                name={`mtno${i}`}
                                                                label="NOVA_JMENOVITAHODNOTA"
                                                                onChangeCustom={onCelkovaHodnotaEmiseChange}
                                                                maxLength={16}
                                                                precision={2}
                                                                rules={dveDesetinneMistaValidation()}
                                                                initialValue={item?.mtno}
                                                                notRequired={true}
                                                                disabled={displayOnly}
                                                            />
                                                            <SelectComponent
                                                                isFormItem={true}
                                                                name={`dnom${i}`}
                                                                defaultValue={item?.dnom}
                                                                labelParagraph="MENA_JMENOVITEHODNOTY"
                                                                data={cisMeny}
                                                                notRequired={true}
                                                                disabled={displayOnly}
                                                            />
                                                            <DatePickerComponent
                                                                name={`datumsplatnosti${i}`}
                                                                label="DATUM_SPLACENI_EMISE"
                                                                initialValue={
                                                                    item.datumsplatnosti
                                                                        ? dayjs(item.datumsplatnosti)
                                                                        : undefined
                                                                }
                                                                disabledDate={current => current < dayjs()}
                                                                defaultValue={
                                                                    item.datumsplatnosti
                                                                        ? dayjs(item.datumsplatnosti)
                                                                        : undefined
                                                                }
                                                                notRequired={true}
                                                                disabled={displayOnly}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </>
                                        )}
                                    <SelectComponent
                                        isFormItem={true}
                                        name="frekvencevyplaty"
                                        defaultValue={udajeEmise?.frekvencevyplaty}
                                        labelParagraph="ISIN_EMISE_DLUHOPIS_FREKVENCE"
                                        data={frekvenceVyplatySelect}
                                        disabled={form.getFieldValue('urokovasazba') === EUrokovaSazba.Z || displayOnly}
                                    />
                                    <SelectComponent
                                        isFormItem={true}
                                        name="zaruceniemise"
                                        defaultValue={udajeEmise?.zaruceniemise}
                                        labelParagraph="ISIN_EMISE_DLUHOPIS_ZARUCENI"
                                        data={
                                            druhCP === EDruhCp.DLUHOPIS
                                                ? form.getFieldValue('podrizeny')
                                                    ? zarucenieEmisePodrizenySelect
                                                    : zarucenieEmiseSelectAlt
                                                : zarucenieEmiseSelect
                                        }
                                        infoTooltipText={
                                            druhCP === EDruhCp.DLUHOPIS && !displayOnly
                                                ? form.getFieldValue('druhdluhopisu') === EDruhDluhopisu.OK ||
                                                  form.getFieldValue('druhdluhopisu') === EDruhDluhopisu.OJ
                                                    ? 'ISIN_EMISE_DLUHOPIS_ODDELENI_KUPONU_HELP'
                                                    : undefined
                                                : undefined
                                        }
                                        disabled={displayOnly}
                                    />
                                </>
                            )}
                            {druhCP === EDruhCp.NASTROJKOLINV && (
                                <>
                                    <SelectComponent
                                        defaultValue={udajeEmise?.zasadyvyplaty}
                                        isFormItem={true}
                                        name="zasadyvyplaty"
                                        labelParagraph="ISIN_EMISE_KOLEKTIVNI_ZASADY"
                                        data={zasadyVyplatySelect}
                                        disabled={displayOnly}
                                    />

                                    <SelectComponent
                                        isFormItem={true}
                                        defaultValue={udajeEmise?.druhfondinvestuje}
                                        name="druhfondinvestuje"
                                        labelParagraph="ISIN_EMISE_LIST_AKTIVA"
                                        data={druhInvesticieSelect}
                                        disabled={displayOnly}
                                    />

                                    <SelectComponent
                                        isFormItem={true}
                                        defaultValue={udajeEmise?.typcpproinvestory}
                                        name="typcpproinvestory"
                                        labelParagraph="ISIN_EMISE_KOLEKTIVNI_TYP"
                                        data={typCennehoPapiruSelect}
                                        disabled={displayOnly}
                                    />
                                    <SelectComponent
                                        isFormItem={true}
                                        name="dnom"
                                        defaultValue={udajeEmise?.dnom}
                                        labelParagraph="MENA"
                                        data={cisMeny}
                                        disabled={displayOnly}
                                    />
                                    <InputComponent
                                        label="DODATEK_INVCERT_DRUH_AKCII"
                                        name="druhdlefondu"
                                        infoTooltipText={!displayOnly ? 'DODATEK_INVCERT_DRUH_AKCII_TOOLTIP' : ''}
                                        initialValue={udajeEmise?.druhdlefondu}
                                        maxLength={50}
                                        rules={[
                                            () => ({
                                                validator(_, value: string) {
                                                    if (!value || globalInputRegExp.test(value))
                                                        return Promise.resolve();
                                                    return Promise.reject(
                                                        <TranslateText>VALIDACE_GLOBAL_INPUT</TranslateText>
                                                    );
                                                }
                                            })
                                        ]}
                                        disabled={displayOnly}
                                    />
                                </>
                            )}
                            {druhCP === EDruhCp.PODILLIST && (
                                <>
                                    <SelectComponent
                                        isFormItem={true}
                                        name="pouzitizisku"
                                        defaultValue={udajeEmise?.pouzitizisku}
                                        labelParagraph="ISIN_EMISE_LIST_ZISK"
                                        data={pouzitiZiskuSelect}
                                        disabled={displayOnly}
                                    />

                                    <SelectComponent
                                        isFormItem={true}
                                        name="druhaktivit"
                                        defaultValue={udajeEmise?.druhaktivit}
                                        labelParagraph="ISIN_EMISE_LIST_AKTIVA"
                                        data={druhInvesticieSelect}
                                        disabled={displayOnly}
                                    />

                                    <SelectComponent
                                        isFormItem={true}
                                        name="typpodillistu"
                                        defaultValue={udajeEmise?.typpodillistu}
                                        labelParagraph="ISIN_EMISE_LIST_TYP"
                                        data={otevrenyUzavrenySelect}
                                        disabled={displayOnly}
                                    />

                                    <SelectComponent
                                        isFormItem={true}
                                        name="omezeniinvest"
                                        defaultValue={udajeEmise?.omezeniinvest}
                                        labelParagraph="ISIN_EMISE_LIST_OMEZENI"
                                        data={[
                                            {
                                                value: 'RK',
                                                labelcz: 'ISIN_EMISE_LIST_OMEZENI_RETAIL'
                                            },
                                            {
                                                value: 'JK',
                                                labelcz: 'ISIN_EMISE_LIST_OMEZENI_KVALIF'
                                            }
                                        ]}
                                        disabled={displayOnly}
                                    />
                                    <InputComponent
                                        label="ISIN_EMISE_LIST_TRIDA_FONDU"
                                        name="tridapl"
                                        infoTooltipText={!displayOnly ? 'ISIN_EMISE_LIST_TRIDA_FONDU_TOOLTIP' : ''}
                                        initialValue={udajeEmise?.tridapl}
                                        disabled={displayOnly}
                                        maxLength={50}
                                    />
                                </>
                            )}
                            {druhCP === EDruhCp.WARRANT && (
                                <>
                                    <SelectComponent
                                        isFormItem={true}
                                        name="druhwarrantu"
                                        defaultValue={udajeEmise?.druhwarrantu}
                                        labelParagraph="ISIN_EMISE_WARRANT_DRUH"
                                        data={[
                                            {
                                                value: EDruhCertifikatu.PAKOVY,
                                                labelcz: 'ISIN_EMISE_CERTIFIKAT_DRUH_PAKOVY'
                                            },
                                            {
                                                value: EDruhCertifikatu.NEPAKOVY,
                                                labelcz: 'ISIN_EMISE_CERTIFIKAT_DRUH_NEPAKOVY'
                                            }
                                        ]}
                                        onChange={rerender}
                                        disabled={displayOnly}
                                    />
                                    {(form.getFieldValue('evidencecp') === EEvidenceCp.CDCP ||
                                        form.getFieldValue('evidencecp') === EEvidenceCp.CDCPDLT) && (
                                        <>
                                            {form.getFieldValue('druhwarrantu') === EDruhCertifikatu.PAKOVY && (
                                                <>
                                                    <InputNumberComponent
                                                        label="STRIKE_CENA"
                                                        name="strikecena"
                                                        rules={dveDesetinneMistaValidation()}
                                                        initialValue={udajeEmise?.strikecena}
                                                        disabled={displayOnly}
                                                    />
                                                    <SelectComponent
                                                        isFormItem={true}
                                                        name="strikemena"
                                                        defaultValue={udajeEmise?.strikemena}
                                                        labelParagraph="STRIKE_MENA"
                                                        data={cisMeny}
                                                        disabled={displayOnly}
                                                    />
                                                </>
                                            )}
                                            {form.getFieldValue('druhwarrantu') && (
                                                <>
                                                    <InputNumberComponent
                                                        label="VYMENNY_POMER"
                                                        infoTooltipText={!displayOnly ? 'VYMENNY_POMER_TOOTLTIP' : ''}
                                                        name="multiplikator"
                                                        initialValue={udajeEmise?.multiplikator}
                                                        precision={6}
                                                        notRequired={
                                                            form.getFieldValue('druhwarrantu') ===
                                                            EDruhCertifikatu.NEPAKOVY
                                                        }
                                                        disabled={displayOnly}
                                                    />
                                                    <SelectComponent
                                                        isFormItem={true}
                                                        name="konecvyporadani"
                                                        defaultValue={udajeEmise?.konecvyporadani}
                                                        labelParagraph="ISIN_EMISE_WARRANT_KONEC_VYPORADANI"
                                                        data={konecVyporadaniSelect}
                                                        disabled={displayOnly}
                                                    />
                                                    <SelectComponent
                                                        isFormItem={true}
                                                        name="menavyporadani"
                                                        defaultValue={udajeEmise?.menavyporadani}
                                                        labelParagraph="MENA_VYPORADANI"
                                                        data={cisMeny}
                                                        disabled={displayOnly}
                                                    />
                                                </>
                                            )}
                                        </>
                                    )}

                                    <SelectComponent
                                        isFormItem={true}
                                        name="podminkysdatumem"
                                        defaultValue={
                                            typeof udajeEmise?.podminkysdatumem === 'boolean'
                                                ? String(udajeEmise.podminkysdatumem)
                                                : udajeEmise?.podminkysdatumem
                                        }
                                        labelParagraph="ISIN_EMISE_WARRANT_EMISNI_PODMINKY"
                                        data={trueFalseSelect}
                                        disabled={displayOnly}
                                        onChange={() => rerender()}
                                    />

                                    {form.getFieldValue('podminkysdatumem') === ETrueFalse.TRUE && (
                                        <DatePickerComponent
                                            name="datumsplatnosti"
                                            label="ISIN_EMISE_DLUHOPIS_SPLATNOST"
                                            initialValue={
                                                udajeEmise?.datumsplatnosti
                                                    ? dayjs(udajeEmise.datumsplatnosti)
                                                    : undefined
                                            }
                                            disabledDate={current => current < dayjs()}
                                            onChange={() => rerender()}
                                        />
                                    )}

                                    <SelectComponent
                                        isFormItem={true}
                                        name="predcassplatnost"
                                        defaultValue={
                                            typeof udajeEmise?.predcassplatnost === 'boolean'
                                                ? String(udajeEmise.predcassplatnost)
                                                : udajeEmise?.predcassplatnost
                                        }
                                        labelParagraph="ISIN_EMISE_CERTIFIKAT_SPLATNOST"
                                        data={trueFalseSelect}
                                        disabled={displayOnly}
                                    />
                                    <SelectComponent
                                        isFormItem={true}
                                        name="zaruceniemisewar"
                                        defaultValue={udajeEmise?.zaruceniemisewar}
                                        labelParagraph="ISIN_EMISE_WARRANT_ZARUCENI"
                                        data={zarucenieEmiseSelect}
                                        disabled={displayOnly}
                                    />

                                    <SelectComponent
                                        isFormItem={true}
                                        name="frekvencevyplaty"
                                        defaultValue={udajeEmise?.frekvencevyplaty}
                                        labelParagraph="ISIN_EMISE_DLUHOPIS_FREKVENCE"
                                        data={frekvenceVyplatySelect}
                                        disabled={form.getFieldValue('urokovasazba') === EUrokovaSazba.Z || displayOnly}
                                    />
                                </>
                            )}
                        </>
                    )}
                {!isFirstStep && druhCP && (
                    <>
                        {form.getFieldValue('druhakcie') === EDruhAkcie.JINA && (
                            <InputTextArea
                                rows={4}
                                name="akciepopis"
                                initialValue={udajeEmise?.akciepopis}
                                label="JINY_DRUH_AKCIE_POPIS"
                                disabled={displayOnly}
                            />
                        )}
                        {zmenaIsin && (
                            <InputTextArea
                                rows={4}
                                name="duvod"
                                initialValue={udajeEmise?.duvod}
                                label={'ISIN_ZMENA_DUVOD'}
                                disabled={displayOnly}
                            />
                        )}
                        {!displayOnly && (
                            <Form.Item
                                style={{
                                    marginTop: 16,
                                    marginBottom: 40
                                }}
                                name="identifikaceosoby"
                                validateTrigger="onSubmit"
                                label={<Paragraph text={'SOUBOR'} strong={true} />}
                                rules={[
                                    {
                                        required: true,
                                        validator() {
                                            try {
                                                if (fileList && fileList.length < 1)
                                                    return Promise.reject(
                                                        <TranslateText>NEVALIDNI_POVINNA_POLOZKA_UPLOAD</TranslateText>
                                                    );
                                                return Promise.resolve();
                                            } catch {
                                                return Promise.reject();
                                            }
                                        }
                                    }
                                ]}
                            >
                                <div
                                    style={{
                                        marginBottom: 22
                                    }}
                                >
                                    <Paragraph
                                        style={{
                                            display: 'block',
                                            paddingBottom: 6
                                        }}
                                        text={
                                            druhCP === EDruhCp.AKCIE &&
                                            form.getFieldValue('druhakcie') !== EDruhAkcie.JINA
                                                ? 'ISIN_EMISE_SOUBORY_AKCIE_HELP'
                                                : druhCP === EDruhCp.DLUHOPIS
                                                  ? 'ISIN_EMISE_SOUBORY_DLUHOPIS_HELP'
                                                  : druhCP === EDruhCp.JINYINVNASTROJ ||
                                                      druhCP === EDruhCp.PREDNOSTNIPRAVO ||
                                                      druhCP === EDruhCp.PRAVOUPSANI ||
                                                      druhCP === EDruhCp.ZATIMNILISTY ||
                                                      druhCP === EDruhCp.OSTATNI ||
                                                      form.getFieldValue('druhakcie') === EDruhAkcie.JINA
                                                    ? 'ISIN_EMISE_SOUBORY_JINY_HELP'
                                                    : druhCP === EDruhCp.NASTROJKOLINV
                                                      ? 'ISIN_EMISE_SOUBORY_LIST_HELP'
                                                      : druhCP === EDruhCp.PODILLIST
                                                        ? 'ISIN_EMISE_SOUBORY_LIST_HELP'
                                                        : druhCP === EDruhCp.WARRANT || druhCP === EDruhCp.INVCERT
                                                          ? 'ISIN_EMISE_SOUBORY_WARRANT_HELP'
                                                          : ''
                                        }
                                    />
                                    <Paragraph
                                        style={{
                                            display: 'block',
                                            paddingBottom: 6
                                        }}
                                        text={'Pokud je to možné, přiložte editovatelné soubory.'}
                                    />
                                    <Paragraph
                                        style={{
                                            display: 'block',
                                            paddingBottom: 6
                                        }}
                                        text={'ISIN_UPLOAD_TEXT'}
                                    />
                                    <UploadComponent
                                        {...uploadProps}
                                        buttonId="isinUploadComponentBtn"
                                        disabled={displayOnly}
                                    />
                                </div>
                            </Form.Item>
                        )}
                    </>
                )}
                {!displayOnly && (
                    <Row className="button-row">
                        <Col>
                            <ButtonComponent
                                type="default"
                                buttontext="ZPET"
                                onClick={() =>
                                    isFirstStep
                                        ? prevStep()
                                        : (setIsFirstStep(true),
                                          form.setFieldsValue({
                                              druhakcie: undefined
                                          }))
                                }
                                id="isinZpetBtn"
                            />
                        </Col>
                        <Col>
                            <ButtonComponent
                                buttontext="POKRACOVAT"
                                onClick={() => form.submit()}
                                id="isinPokracovatBtn"
                                loading={uploadLoading}
                            />
                        </Col>
                    </Row>
                )}
            </FormWrapper>
        </LoadingWrapper>
    );
};

export default UdajeEmise;
